import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Edit2, Check, X } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { authenticatedRequest } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth-context";

interface EditableUserNameProps {
  currentName: string;
}

export function EditableUserName({ currentName }: EditableUserNameProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState("");
  const { toast } = useToast();
  const { refreshUser } = useAuth();

  const displayName = currentName || "Unnamed User";

  const updateNameMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await authenticatedRequest('PATCH', '/api/users/me/name', { name });
      return await res.json();
    },
    onSuccess: () => {
      toast({
        title: "Name updated",
        description: "Your name has been updated successfully.",
      });

      // Refresh auth context to get updated user data
      refreshUser();
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating name",
        description: error.message || "Failed to update name. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleStartEdit = () => {
    setNewName(currentName || "");
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmedName = newName.trim();
    if (trimmedName && trimmedName !== currentName) {
      updateNameMutation.mutate(trimmedName);
    } else {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setNewName("");
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 flex-1">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={handleKeyPress}
          className="text-lg font-semibold"
          autoFocus
          maxLength={100}
          placeholder="Enter your name"
        />
        <Button
          size="sm"
          onClick={handleSave}
          disabled={updateNameMutation.isPending || !newName.trim()}
          className="h-8 w-8 p-0"
        >
          {updateNameMutation.isPending ? (
            <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleCancel}
          disabled={updateNameMutation.isPending}
          className="h-8 w-8 p-0"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 group">
      <p className="font-semibold text-lg">{displayName}</p>
      <Button
        size="sm"
        variant="ghost"
        onClick={handleStartEdit}
        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white/20 hover:text-inherit"
      >
        <Edit2 className="w-3 h-3" />
      </Button>
    </div>
  );
}
